/**
 * ButtonExamples.tsx
 * Comprehensive examples of HeroUI Button component
 * 
 * Showcases all HeroUI Button features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Button } from '@enfomentis/ui';
import { Card, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { 
  Download, 
  Upload, 
  Save, 
  Trash2, 
  Edit, 
  Plus, 
  Search, 
  Settings, 
  ChevronDown,
  Heart,
  Share,
  Play,
  Pause,
  Star,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';


export const ButtonExamples: React.FC = () => {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [likedStates, setLikedStates] = useState<Record<string, boolean>>({});

  const toggleLoading = (buttonId: string) => {
    setLoadingStates(prev => ({ ...prev, [buttonId]: !prev[buttonId] }));
    // Simulate async operation
    setTimeout(() => {
      setLoadingStates(prev => ({ ...prev, [buttonId]: false }));
    }, 2000);
  };

  const toggleLike = (buttonId: string) => {
    setLikedStates(prev => ({ ...prev, [buttonId]: !prev[buttonId] }));
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Variants</h3>
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-center gap-4">
                <Button color="primary">Primary</Button>
                <Button color="secondary">Secondary</Button>
                <Button variant="bordered" color="primary">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button color="danger">Destructive</Button>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Button Sizes</h3>
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-center gap-4">
                <Button color="primary" size="sm">Small</Button>
                <Button color="primary" size="md">Medium</Button>
                <Button color="primary" size="lg">Large</Button>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Buttons with Icons</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Start Icons</p>
                  <div className="flex flex-wrap gap-3">
                    <Button color="primary">
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                    <Button color="secondary">
                      <Upload className="w-4 h-4" />
                      Upload
                    </Button>
                    <Button variant="bordered" color="primary">
                      <Save className="w-4 h-4" />
                      Save
                    </Button>
                    <Button color="danger">
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-text-secondary mb-2">End Icons</p>
                  <div className="flex flex-wrap gap-3">
                    <Button color="primary">
                      Next
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button variant="bordered" color="primary">
                      Settings
                      <Settings className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Icon Only Buttons</p>
                  <div className="flex flex-wrap gap-3">
                    <Button color="primary" aria-label="Edit item" className="w-10 h-10 p-0 flex items-center justify-center min-w-10">
                      <Edit className="w-4 h-4 shrink-0" />
                    </Button>
                    <Button color="secondary" aria-label="Search" className="w-10 h-10 p-0 flex items-center justify-center min-w-10">
                      <Search className="w-4 h-4 shrink-0" />
                    </Button>
                    <Button variant="bordered" color="primary" aria-label="Add new item" className="w-10 h-10 p-0 flex items-center justify-center min-w-10">
                      <Plus className="w-4 h-4 shrink-0" />
                    </Button>
                    <Button variant="ghost" aria-label="Open settings" className="w-10 h-10 p-0 flex items-center justify-center min-w-10">
                      <Settings className="w-4 h-4 shrink-0" />
                    </Button>
                  </div>
                  
                  <div className="mt-3">
                    <p className="text-sm text-text-secondary mb-2">Different Sizes - Icon Only</p>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button color="primary" size="sm" aria-label="Small edit" className="w-8 h-8 p-0 flex items-center justify-center min-w-8">
                        <Edit className="w-4 h-4 shrink-0" />
                      </Button>
                      <Button color="primary" size="md" aria-label="Medium edit" className="w-10 h-10 p-0 flex items-center justify-center min-w-10">
                        <Edit className="w-4 h-4 shrink-0" />
                      </Button>
                      <Button color="primary" size="lg" aria-label="Large edit" className="w-12 h-12 p-0 flex items-center justify-center min-w-12">
                        <Edit className="w-5 h-5 shrink-0" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-3">
                    <p className="text-sm text-text-secondary mb-2">Rounded Icon Buttons</p>
                    <div className="flex flex-wrap gap-3">
                      <Button color="primary" aria-label="Download" className="w-12 h-12 p-0 flex items-center justify-center rounded-full min-w-12">
                        <Download className="w-4 h-4 shrink-0" />
                      </Button>
                      <Button color="secondary" aria-label="Upload" className="w-12 h-12 p-0 flex items-center justify-center rounded-full min-w-12">
                        <Upload className="w-4 h-4 shrink-0" />
                      </Button>
                      <Button variant="bordered" color="primary" aria-label="Save" className="w-12 h-12 p-0 flex items-center justify-center rounded-full min-w-12">
                        <Save className="w-4 h-4 shrink-0" />
                      </Button>
                      <Button color="danger" aria-label="Delete" className="w-12 h-12 p-0 flex items-center justify-center rounded-full min-w-12">
                        <Trash2 className="w-4 h-4 shrink-0" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Button States</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Disabled State</p>
                  <div className="flex flex-wrap gap-3">
                    <Button color="primary" isDisabled>Disabled Primary</Button>
                    <Button color="secondary" isDisabled>Disabled Secondary</Button>
                    <Button variant="bordered" color="primary" isDisabled>Disabled Outline</Button>
                    <Button variant="ghost" isDisabled>Disabled Ghost</Button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Loading State</p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      color="primary" 
                      isLoading={loadingStates['load1'] || false}
                      onPress={() => toggleLoading('load1')}
                    >
                      {loadingStates['load1'] ? 'Loading...' : 'Click to Load'}
                    </Button>
                    <Button 
                      color="secondary" 
                      isLoading={loadingStates['load2'] || false}
                      onPress={() => toggleLoading('load2')}
                    >
                      <Upload className="w-4 h-4" />
                      {loadingStates['load2'] ? 'Uploading...' : 'Upload File'}
                    </Button>
                    <Button 
                      variant="bordered" color="primary" 
                      isLoading={loadingStates['load3'] || false}
                      onPress={() => toggleLoading('load3')}
                    >
                      {loadingStates['load3'] ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Interactive Buttons</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Toggle Buttons</p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      variant={likedStates['heart'] ? "solid" : "bordered"}
                      color="primary"
                      onPress={() => toggleLike('heart')}
                    >
                      <Heart className={`w-4 h-4 ${likedStates['heart'] ? 'fill-current' : ''}`} />
                      {likedStates['heart'] ? 'Liked' : 'Like'}
                    </Button>
                    <Button 
                      variant={likedStates['star'] ? "solid" : "bordered"}
                      color="primary"
                      onPress={() => toggleLike('star')}
                    >
                      <Star className={`w-4 h-4 ${likedStates['star'] ? 'fill-current' : ''}`} />
                      {likedStates['star'] ? 'Starred' : 'Star'}
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Action Buttons</p>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="bordered" color="primary" onPress={() => navigator.share?.({title: 'Shared!', text: 'Check this out'})}>
                      <Share className="w-4 h-4" />
                      Share
                    </Button>
                    <Button color="primary" onPress={() => console.log('Playing...')}>
                      <Play className="w-4 h-4" />
                      Play
                    </Button>
                    <Button color="secondary" onPress={() => console.log('Paused')}>
                      <Pause className="w-4 h-4" />
                      Pause
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Button Compositions</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Buttons with Badges</p>
                  <div className="flex flex-wrap gap-4">
                    <Badge content="5" color="danger">
                      <Button variant="bordered" color="primary">
                        Notifications
                      </Button>
                    </Badge>
                    <Badge content="new" color="success" size="sm">
                      <Button color="primary">
                        Latest Feature
                      </Button>
                    </Badge>
                    <Badge content="" color="warning">
                      <Button color="secondary">
                        Pending Review
                      </Button>
                    </Badge>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Button Groups</p>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="bordered" color="primary" size="sm">First</Button>
                    <Button variant="bordered" color="primary" size="sm">Second</Button>
                    <Button variant="bordered" color="primary" size="sm">Third</Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Full Width Buttons</h3>
          <Card>
            <CardBody>
              <div className="space-y-3">
                <Button color="primary" className="w-full">
                  Full Width Primary
                </Button>
                <Button variant="bordered" color="primary" className="w-full">
                  <Download className="w-4 h-4" />
                  Full Width with Icon
                </Button>
                <Button color="secondary" className="w-full" size="lg">
                  Large Full Width Button
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Use Cases</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Trade Actions</p>
                  <div className="flex flex-wrap gap-3">
                    <Button color="primary">
                      <TrendingUp className="w-4 h-4" />
                      Buy
                    </Button>
                    <Button color="danger">
                      <TrendingDown className="w-4 h-4" />
                      Sell
                    </Button>
                    <Button variant="bordered" color="primary">
                      <Plus className="w-4 h-4" />
                      New Trade
                    </Button>
                    <Button color="secondary">
                      <Edit className="w-4 h-4" />
                      Edit Position
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Status Actions</p>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="bordered" color="primary">
                      <AlertTriangle className="w-4 h-4 text-warning" />
                      Risk Alert
                    </Button>
                    <Button variant="bordered" color="primary">
                      <CheckCircle className="w-4 h-4 text-success" />
                      Target Hit
                    </Button>
                    <Button variant="ghost">
                      <Settings className="w-4 h-4" />
                      Strategy Settings
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Export & Analysis</p>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="bordered" color="primary">
                      <Download className="w-4 h-4" />
                      Export Trades
                    </Button>
                    <Button color="secondary">
                      <Save className="w-4 h-4" />
                      Save Session
                    </Button>
                    <Button color="primary">
                      Generate Report
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Advanced Button Features</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Accessibility Features</p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      color="primary" 
                      aria-label="Save document with keyboard shortcut Ctrl+S"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </Button>
                    <Button 
                      variant="bordered" color="primary" 
                      aria-label="Delete selected items permanently"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Focus Management</p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      color="primary" 
                      onPress={() => document.querySelector<HTMLButtonElement>('[data-focus-target]')?.focus()}
                    >
                      Focus Next Button
                    </Button>
                    <Button 
                      variant="bordered" color="primary" 
                      data-focus-target
                    >
                      I get focused!
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Custom Styling</p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      color="primary" 
                      className="bg-linear-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                    >
                      Gradient Button
                    </Button>
                    <Button 
                      variant="bordered" color="primary" 
                      className="rounded-full px-6"
                    >
                      Rounded Button
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="text-profit hover:bg-profit-light"
                    >
                      Profit Theme
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};